import { useSyncExternalStore } from 'react';
import type { OnboardingStep } from '@flobi/asset-library/tours';
import { currentTokenGetter } from '../../services/apiClient';

/**
 * What Get started and the tours read from the server, kept here for the deck, the menu row and
 * the tour host alike:
 *   steps      Artwork's Get started steps (users-back onboarding.getSteps, app_slug "artwork")
 *   viewPrefs  the person's view prefs (users.preferences), where the tours keep their progress
 *
 * Plain fetches that only throw, like the hints' (ArtworkHintsHost quietCall): apiClient sends a
 * 401 to sign-in, and a step list that can't load should just stay empty.
 */
const GATEWAY = (import.meta.env.VITE_BACKEND_URL as string | undefined) ?? '';
const APP_SLUG = 'artwork';

export interface OnboardingState {
  steps: OnboardingStep[];
  loaded: boolean;
  viewPrefs: Record<string, unknown> | null;
}

let state: OnboardingState = { steps: [], loaded: false, viewPrefs: null };
const listeners = new Set<() => void>();
const set = (patch: Partial<OnboardingState>) => {
  state = { ...state, ...patch };
  listeners.forEach((fn) => fn());
};
const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
};
const read = () => state;

/** { steps, loaded, viewPrefs }, kept current. */
export const useOnboarding = () => useSyncExternalStore(subscribe, read, read);

async function call(method: 'GET' | 'POST', path: string, data?: Record<string, string> | Record<string, unknown>): Promise<any> {
  const token = await currentTokenGetter()?.().catch(() => null);
  const query = method === 'GET' && data ? `?${new URLSearchParams(data as Record<string, string>)}` : '';
  const res = await fetch(`${GATEWAY}${path}${query}`, {
    method,
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...(token ? { authorization: `Bearer ${token}` } : {}) },
    body: method === 'GET' ? undefined : JSON.stringify(data ?? {}),
  });
  const body = (await res.json().catch(() => null)) as { success?: boolean; data?: unknown } | null;
  if (!res.ok || !body || body.success === false) throw new Error(`${path} ${res.status}`);
  return typeof body === 'object' && 'data' in body ? body.data : body;
}

/** Read the steps again (after a tick, or when the deck comes into view). */
export async function loadSteps(): Promise<void> {
  try {
    const data = await call('GET', '/users/onboarding.getSteps', { app_slug: APP_SLUG });
    set({ steps: Array.isArray(data?.steps) ? data.steps : [], loaded: true });
  } catch {
    set({ loaded: true });
  }
}

export async function loadViewPrefs(): Promise<void> {
  try {
    const prefs = await call('POST', '/users/users.preferences.get', {});
    set({ viewPrefs: prefs?.viewPrefs ?? {} });
  } catch {
    // Without them the tours start from nothing; a save later still merges on the server.
  }
}

/** Write one view pref (the server merges viewPrefs one key deep). Shown at once; the server's row is what stays. */
export async function saveViewPref(key: string, value: unknown): Promise<void> {
  set({ viewPrefs: { ...(state.viewPrefs ?? {}), [key]: value } });
  try {
    const prefs = await call('POST', '/users/users.preferences.update', { viewPrefs: { [key]: value } });
    if (prefs?.viewPrefs) set({ viewPrefs: prefs.viewPrefs });
  } catch {
    // Kept for this visit; the next save sends the whole object again.
  }
}

/** Tick a Get started step and pay its credits once (onboarding.completeByAction), then read the steps again. */
export async function completeStep(actionKey: string): Promise<void> {
  if (!actionKey) return;
  try {
    await call('POST', '/users/onboarding.completeByAction', { action_key: actionKey });
  } catch {
    // A step the server doesn't have yet stays as it is.
  }
  await loadSteps();
}

/** Signed out: nothing of the last person stays. */
export const resetOnboarding = () => set({ steps: [], loaded: false, viewPrefs: null });
